import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './lib/auth';
import { useAppConfig } from './lib/api';
import { getRefreshGate } from './lib/appRefresh';
import { RefreshController } from './components/AppRefresh';
import { LoginPage } from './pages/Login';
import { SignUpPage } from './pages/SignUp';
import { ConfirmSignUpPage } from './pages/ConfirmSignUp';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ModulesPage } from './pages/Modules';
import { StudyPage } from './pages/Study';
import { ProfilePage } from './pages/Profile';
import { LeaderboardPage } from './pages/Leaderboard';
import { UploadPage } from './pages/Upload';
import { ClassPage } from './pages/Class';
import { TeachPage } from './pages/Teach';
import { JoinClassPage } from './pages/JoinClass';
import { ExamsPage } from './pages/Exams';
import { ExamDashboardPage } from './pages/ExamDashboard';
import { ExamSessionsPage } from './pages/ExamSessions';
import { ExamTopicsPage } from './pages/ExamTopics';
import { ExamLeaderboardPage } from './pages/ExamLeaderboard';
import { OnboardingPage } from './pages/Onboarding';
import { FriendsPage } from './pages/Friends';
import { PrivacyPage } from './pages/Privacy';
import { TermsPage } from './pages/Terms';
import { CookiePolicyPage } from './pages/CookiePolicy';
import { SharedQuestionPage } from './pages/SharedQuestion';
import { LearnPage } from './pages/Learn';
import { CriticalThinkingPage } from './pages/CriticalThinking';
import { CriticalThinkingDetailPage } from './pages/CriticalThinkingDetail';
import { FlashCardsPage } from './pages/FlashCards';
import { QuickMemoryPage } from './pages/QuickMemory';
import { StudyGroupsPage } from './pages/StudyGroups';
import { StudyGroupCreatePage } from './pages/StudyGroupCreate';
import { StudyGroupLobbyPage } from './pages/StudyGroupLobby';
import { StudyGroupPlayPage } from './pages/StudyGroupPlay';
import { StudyGroupResultsPage } from './pages/StudyGroupResults';
import { StudyGroupReviewPage } from './pages/StudyGroupReview';
import { StudyGroupJoinPage } from './pages/StudyGroupJoin';
import { AppShell } from './components/AppShell';

export default function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { data: appConfig, refetch: refetchAppConfig } = useAppConfig();
  const queryClient = useQueryClient();
  const [nativeVersion, setNativeVersion] = useState<string | null>(null);
  const refreshGate = useMemo(() => getRefreshGate(appConfig?.policy, nativeVersion), [appConfig?.policy, nativeVersion]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.getInfo().then((info) => setNativeVersion(info.version)).catch(() => {});
      ScreenOrientation.lock({ orientation: 'portrait' }).catch(() => {});
    }

    // On foreground/focus, refresh the policy AND live social data. React Query's
    // own refetch-on-focus is unreliable in the iOS WebView, so we invalidate
    // explicitly here — this is what makes friend requests/acceptances (and the
    // remove trash) show up without a force-close.
    const onActive = () => {
      refetchAppConfig();
      queryClient.invalidateQueries({ queryKey: ['friends'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    };
    window.addEventListener('focus', onActive);
    document.addEventListener('visibilitychange', onActive);

    let removeAppState: (() => void) | undefined;
    let removeUrlOpen: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) onActive();
      }).then((handle) => { removeAppState = () => handle.remove(); }).catch(() => {});

      // Universal / App Links: a tapped squishyschool.com link opens the app
      // (not the browser); route the SPA to the matching path.
      CapacitorApp.addListener('appUrlOpen', (event) => {
        try {
          const u = new URL(event.url);
          if (u.pathname && u.pathname !== '/') navigate(u.pathname + u.search);
        } catch { /* ignore non-URL opens */ }
      }).then((handle) => { removeUrlOpen = () => handle.remove(); }).catch(() => {});
    }

    return () => {
      window.removeEventListener('focus', onActive);
      document.removeEventListener('visibilitychange', onActive);
      removeAppState?.();
      removeUrlOpen?.();
    };
  }, [refetchAppConfig, navigate, queryClient]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500">
        Loading…
      </div>
    );
  }

  return (
    <>
    <RefreshController gate={refreshGate} />
    <Routes>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/q/:shareId" element={<SharedQuestionPage />} />
      {!user ? (
        <>
          <Route path="/login"   element={<LoginPage />} />
          <Route path="/signup"  element={<SignUpPage />} />
          <Route path="/confirm" element={<ConfirmSignUpPage />} />
          <Route path="/forgot"  element={<ForgotPasswordPage />} />
          <Route path="*" element={<RedirectToLogin />} />
        </>
      ) : (
        <Route element={<AppShell />}>
          <Route path="/" element={<ModulesPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/study/:contextType/:contextId" element={<StudyPage />} />
          <Route path="/flashcards/:sectionId" element={<FlashCardsPage />} />
          <Route path="/quickmem/:sectionId" element={<QuickMemoryPage />} />
          <Route path="/class/:sectionId" element={<ClassPage />} />
          <Route path="/teach" element={<TeachPage />} />
          {/* Invite links are shared publicly; RedirectToLogin preserves the
              path so the code survives a login round trip. */}
          <Route path="/join" element={<JoinClassPage />} />
          <Route path="/join/:code" element={<JoinClassPage />} />
          <Route path="/exams" element={<ExamsPage />} />
          <Route path="/exam/:examId" element={<ExamDashboardPage />} />
          <Route path="/exam/:examId/sessions" element={<ExamSessionsPage />} />
          <Route path="/exam/:examId/topics" element={<ExamTopicsPage />} />
          <Route path="/exam/:examId/leaderboard" element={<ExamLeaderboardPage />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/learn/:discipline" element={<CriticalThinkingPage />} />
          <Route path="/learn/:discipline/:itemId" element={<CriticalThinkingDetailPage />} />
          <Route path="/study-groups" element={<StudyGroupsPage />} />
          <Route path="/study-groups/create" element={<StudyGroupCreatePage />} />
          <Route path="/study-groups/:id/lobby" element={<StudyGroupLobbyPage />} />
          <Route path="/study-groups/:id/play" element={<StudyGroupPlayPage />} />
          <Route path="/study-groups/:id/results" element={<StudyGroupResultsPage />} />
          <Route path="/study-groups/:id/review" element={<StudyGroupReviewPage />} />
          <Route path="/groups/join/:roomCode" element={<StudyGroupJoinPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/leaderboard/:scope" element={<LeaderboardPage />} />
          <Route path="/friends" element={<FriendsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
    </>
  );
}

/** Redirect unauthenticated users to /login, preserving the current path as returnUrl */
function RedirectToLogin() {
  const loc = useLocation();
  const path = loc.pathname + loc.search;
  const to = path && path !== '/' ? `/login?returnUrl=${encodeURIComponent(path)}` : '/login';
  return <Navigate to={to} replace />;
}
